import { useEffect, useRef, useState } from 'react';
import { adminApi, companyApi } from './api.ts';

export type AbnVerificationResult = {
  status: 'unverified' | 'verified' | 'invalid';
  entityName: string | null;
  registryStatus: string | null;
  addressState: string | null;
  addressPostcode: string | null;
};

export type AbnLiveState = 'idle' | 'checking' | AbnVerificationResult['status'];

const ABN_LENGTH = 11;
const DEBOUNCE_MS = 500;

/**
 * Live ABN verification while typing. Fires only once the input holds exactly
 * 11 digits, debounced 500ms. A monotonic sequence guards against stale
 * responses (an earlier slow lookup landing after a newer keystroke). Pass an
 * empty string to disable (e.g. when the field is locked / not editing).
 */
export function useAbnVerification(rawAbn: string, audience: 'company' | 'admin') {
  const [state, setState] = useState<AbnLiveState>('idle');
  const [result, setResult] = useState<AbnVerificationResult | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const digits = rawAbn.replace(/\D/g, '');
    if (digits.length !== ABN_LENGTH) {
      setState('idle');
      setResult(null);
      return;
    }
    const mySeq = ++seq.current;
    setState('checking');
    const timer = setTimeout(() => {
      const api = audience === 'admin' ? adminApi : companyApi;
      const path = audience === 'admin' ? '/api/companies/abn-lookup' : '/api/me/abn-lookup';
      api
        .get<AbnVerificationResult>(path, { query: { abn: digits } })
        .then((res) => {
          if (mySeq !== seq.current) return; // a newer input superseded this
          setResult(res);
          setState(res.status);
        })
        .catch(() => {
          if (mySeq !== seq.current) return;
          setResult(null);
          setState('unverified');
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rawAbn, audience]);

  return { state, result };
}
